import { ALLOWED_IMAGE_TYPES, MAX_IMAGE_BYTES, MAX_IMAGE_DIMENSION } from '@sf/shared';

export class ImageError extends Error {
  constructor(public reason: 'badType' | 'tooLarge' | 'unreadable') {
    super(reason);
  }
}

export function isAllowedImageType(type: string): boolean {
  return (ALLOWED_IMAGE_TYPES as readonly string[]).includes(type);
}

/** Scale (w, h) so the long edge is at most `max`, keeping the aspect ratio. */
export function targetSize(width: number, height: number, max = MAX_IMAGE_DIMENSION) {
  const longEdge = Math.max(width, height);
  if (longEdge <= max) return { width, height };
  const scale = max / longEdge;
  return { width: Math.round(width * scale), height: Math.round(height * scale) };
}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  type: string,
  quality: number,
): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

async function loadBitmap(
  file: File,
): Promise<{ source: CanvasImageSource; width: number; height: number }> {
  if (typeof createImageBitmap === 'function') {
    const bmp = await createImageBitmap(file);
    return { source: bmp, width: bmp.width, height: bmp.height };
  }
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.src = url;
    await img.decode();
    return { source: img, width: img.naturalWidth, height: img.naturalHeight };
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Compress an image with a canvas: long edge ≤ 1920px, WebP at 0.8 (JPEG fallback).
 * Keeps the original when it is already small enough and smaller than the result.
 */
export async function compressImage(file: File): Promise<File> {
  if (!isAllowedImageType(file.type)) throw new ImageError('badType');

  let bitmap;
  try {
    bitmap = await loadBitmap(file);
  } catch {
    throw new ImageError('unreadable');
  }
  const { width, height } = targetSize(bitmap.width, bitmap.height);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new ImageError('unreadable');
  ctx.drawImage(bitmap.source, 0, 0, width, height);

  let blob = await canvasToBlob(canvas, 'image/webp', 0.8);
  if (!blob || blob.type !== 'image/webp') blob = await canvasToBlob(canvas, 'image/jpeg', 0.8);
  if (!blob) throw new ImageError('unreadable');

  const unchangedSize = width === bitmap.width && height === bitmap.height;
  if (unchangedSize && file.size <= blob.size && file.size < MAX_IMAGE_BYTES) return file;
  if (blob.size >= MAX_IMAGE_BYTES) throw new ImageError('tooLarge');

  const ext = blob.type === 'image/webp' ? 'webp' : 'jpg';
  const base = file.name.replace(/\.[^.]+$/, '') || 'photo';
  return new File([blob], `${base}.${ext}`, { type: blob.type });
}
